// Vue
import { PropType } from "vue";

// Types
import { JSX } from "vue/jsx-runtime";
import { ChartDataType, ChartTooltipEventType, ChartOnClickEventType } from "@/features/Charts";

// ///////////////////
// Common
// ///////////////////

const barChartDataTypeProp = {
  data: {
    type: Object as PropType<ChartDataType>,
    default: () => {},
  },
};

const unitProp = {
  unit: {
    type: String,
    default: "",
  },
};

const onClickProp = {
  onClick: {
    type: Function as PropType<(params: ChartOnClickEventType) => void>,
    default: undefined,
  },
};

const tooltipProp = {
  tooltip: {
    type: Function as PropType<(params: { event: ChartTooltipEventType; unit: string }) => JSX.Element>,
    default:
      () =>
      ({ event }: { event: ChartTooltipEventType; unit: string }) =>
        event?.name,
  },
};

const onHoverProp = {
  onHover: {
    type: Function as PropType<(params: ChartTooltipEventType) => void>,
    default: undefined,
  },
};

const onLeaveProp = {
  onLeave: {
    type: Function as PropType<() => void>,
    default: undefined,
  },
};

const eventMetadataProp = {
  eventMetadata: {
    type: Function as PropType<() => { [key: string]: unknown }>,
    default: () => () => ({}),
  },
};

const settingsProp = {
  settings: {
    type: Object as PropType<{
      realtimeSort?: boolean;
      inverse?: boolean;
      rotateLabels?: 45 | 90 | boolean;
      legend?: boolean;
      stacked?: boolean;
      stackedCategories?: string[];
      // Per-bar label at the end of each bar. "total" adds the row's share of the grand total and
      // needs a chart wide enough to show every row for that share to mean anything (the hotspot
      // deep-dive); "value" prints the number alone, for narrow charts. Omitted/"none" = no label.
      valueLabel?: "none" | "value" | "total";
      // Pixel budget for category-axis names before they truncate (HorizontalBarChart). Widen it
      // where there is room — 120 is enough to render two different rows identically.
      categoryLabelWidth?: number;
      // Tick-density hint for the value axis (HorizontalBarChart), for charts too narrow to carry
      // ECharts' default spacing. It is a hint, not a cap: ECharts still picks the interval it
      // considers "nice", so leave it unset unless the ticks are visibly crowded — on a chart whose
      // data already ends on a round number it can push the axis maximum past that number.
      axisSplitNumber?: number;
      // Pin the value axis (HorizontalBarChart) instead of letting it scale to the data. Opt-in: a
      // normalized chart needs a fixed scale so a bar means the same thing across rows and as series
      // are toggled, while a chart plotting real quantities wants the axis to follow them.
      axisMin?: number;
      axisMax?: number;
      // Treat a data change as a navigation: animate it at the app's motion tokens and respect a
      // reduced-motion preference. See utils/chartMotion.ts.
      motion?: "navigation";
      // Minimum number of category slots (HorizontalBarChart): pads the axis with invisible rows so a
      // one-row drilldown keeps the pitch of the ten-row view instead of centring a lone bar in the
      // card. See utils/padRows.ts.
      minRows?: number;
    }>,
    default: () => ({
      realtimeSort: false,
      inverse: false,
      rotateLabels: false,
      legend: false,
      stacked: false,
      stackedCategories: undefined,
      valueLabel: "none",
      categoryLabelWidth: 120,
    }),
  },
};

// ///////////////////
// VerticalBarChart
// ///////////////////

const verticalBarChartProps = {
  ...onHoverProp,
  ...onLeaveProp,
  ...barChartDataTypeProp,
  ...unitProp,
  ...onClickProp,
  ...tooltipProp,
  ...eventMetadataProp,
  ...settingsProp,
};

// ///////////////////
// HorizontalBarChart
// ///////////////////

const horizontalBarChartProps = {
  ...onHoverProp,
  ...onLeaveProp,
  ...barChartDataTypeProp,
  ...unitProp,
  ...onClickProp,
  ...tooltipProp,
  ...eventMetadataProp,
  ...settingsProp,
};

export { verticalBarChartProps, horizontalBarChartProps };
